import { useState } from "react";
import { 
  Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import { 
  ChevronRight, ChevronLeft, Mail, Link as LinkIcon, Database, Upload, 
  CheckCircle, FileCheck as LucideFileCheck, Building2, BarChart4, Download, 
  Clock, XCircle
} from "lucide-react";
import { Logo } from "@/assets/logo";
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import BASE_URL from "@/constants";

const platformsList = [
  "Amazon", "Flipkart", "BlinkIt", "Swiggy Instamart", 
  "Zepto", "Amazon Tez", "Flipkart Minutes", "BB Now"
];


const getDataHeadersForPlatform = (platform: string): string[] => [
  'Delivery Location', 'Location', 'PO Date', 'PO Number', 'Customer Name',
  'Product Variant ID', 'PO Quantity', 'MRP', 'Unit Price', 'PO Expiry Date',
  'PO Release Date', 'PO Location', 'Ship To Location ID', 'GST', 'SGST', 'IGST'
];

const OnboardingPage = () => {
  const [step, setStep] = useState(1);

  // Step 1: Company
  const [companyData, setCompanyData] = useState({
    companyName: "",
    industry: "",
    erpSystem: "",
  });
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  // Step 2: Admin
  const [adminData, setAdminData] = useState({
    name: "",
    email: "",
    password: "",
    isSuperAdmin: true,
    isManager: false,
    company_name: "",
  });
  const [userId, setUserId] = useState("");
  // Step 3: Platform Config
  const [platformConfigs, setPlatformConfigs] = useState<Record<string, PlatformConfig>>({});
  const [selectedPlatform, setSelectedPlatform] = useState("");
  // Step 4: Approval
  const [platformApprovalStatus, setPlatformApprovalStatus] = useState<Record<string, 'approved' | 'rejected' | 'pending'>>({});
  const [rejectionReasons, setRejectionReasons] = useState<Record<string, string>>({});
  // General
  const [loading, setLoading] = useState(false);
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  // Step Navigation and Progress
  const totalSteps = 5;
  const progress = (step / totalSteps) * 100;

  // ------------------- Handlers ---------------------

  // Company
  interface PlatformConfigs {
    [platform: string]: {
      dataSourceType?: string;
      portalUrl?: string;
      username?: string;
      password?: string;
      apiEndpoint?: string;
      apiKey?: string;
      refreshInterval?: string;
      emailAddress?: string;
      emailFormat?: string;
    };
  }

  const handlePlatformChange = (platform: string, checked: boolean) => {
    setSelectedPlatforms((prev: string[]) =>
      checked ? [...prev, platform] : prev.filter((p) => p !== platform)
    );
    if (!checked) {
      setPlatformConfigs((prev: PlatformConfigs) => {
        const updated = { ...prev };
        delete updated[platform];
        return updated;
      });
      if (selectedPlatform === platform) setSelectedPlatform("");
    }
  };

  // Platform Config
  interface PlatformConfig {
    dataSourceType?: string;
    portalUrl?: string;
    username?: string;
    password?: string;
    apiEndpoint?: string;
    apiKey?: string;
    refreshInterval?: string;
    emailAddress?: string;
    emailFormat?: string;
  }


  const handlePlatformConfigChange = (
    field: keyof PlatformConfig,
    value: string
  ) => {
    if (!selectedPlatform) return;
    setPlatformConfigs(prev => ({
      ...prev,
      [selectedPlatform]: {
        ...prev[selectedPlatform],
        [field]: value
      }
    }));
  };

  const handleDataSourceTypeChange = (type: string) => {
    if (!selectedPlatform) return;
    setPlatformConfigs(prev => ({
      ...prev,
      [selectedPlatform]: {
        dataSourceType: type
        // All other fields will be undefined, so UI will show empty fields
      }
    }));
  };

  // Admin Save (Step 2)
  const handleAdminSave = async () => {
    setLoading(true);
    const payload = {
      name: adminData.name,
      email: adminData.email,
      password: adminData.password,
      role: [],
      isSuperAdmin: "yes",
      isManager: false,
      company_name: companyData.companyName,
    };
    try {
      const res = await fetch(BASE_URL+'/users/CreateUser', {
        method: 'POST',
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.userId || data.id) {
        setUserId(data.userId || data.id);
        setStep(3);
        toast({ title: "Admin profile created." });
      } else {
        throw new Error("No userId returned");
      }
    } catch (err) {
      toast({ title: "Error", description: "Failed to create admin." });
    } finally {
      setLoading(false);
    }
  };

  // Company/Platform Save (Step 3)
  const handleSaveCompanyAndPlatforms = async () => {
    setLoading(true);
    // Build platforms payload
    const platformsPayload = selectedPlatforms.map(platformName => {
      const config = platformConfigs[platformName] || {};
      const dataSourceType = config.dataSourceType || "portal";
      // Map configuration fields to API structure
      let configuration = {};
      if (dataSourceType === "portal") {
        configuration = {
          portalUrl: config.portalUrl,
          username: config.username,
          password: config.password
        };
      } else if (dataSourceType === "api") {
        configuration = {
          apiEndpoint: config.apiEndpoint,
          apiKey: config.apiKey,
          syncInterval: config.refreshInterval ? parseInt(config.refreshInterval) : undefined
        };
      } else if (dataSourceType === "email") {
        configuration = {
          emailAddress: config.emailAddress,
          format: config.emailFormat
        };
      }
      return {
        platformName,
        dataSourceType,
        configuration
      };
    });

    const payload = {
      companyName: companyData.companyName,
      industry: companyData.industry,
      erpSystem: companyData.erpSystem,
      userId,
      platforms: platformsPayload,
      onboardingComplete: false
    };
    try {
      await fetch(BASE_URL+'/onboarding/savecompanyinformation', {
        method: 'POST',
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      setStep(4);
      toast({ title: "Company info saved." });
    } catch (err) {
      toast({ title: "Error", description: "Failed to save company information." });
    } finally {
      setLoading(false);
    }
  };

  // Approval step (UI only)
  interface PlatformApprovalStatus {
    [platform: string]: 'approved' | 'rejected' | 'pending';
  }

  interface RejectionReasons {
    [platform: string]: string;
  }

  const handlePlatformApproval = async (
    platform: string,
    status: 'approved' | 'rejected' | 'pending'
  ) => {
    setPlatformApprovalStatus((prev: PlatformApprovalStatus) => ({ ...prev, [platform]: status }));
    if (status === 'approved') setRejectionReasons((prev: RejectionReasons) => ({ ...prev, [platform]: '' }));
    // For rejection, do not call API here. Wait for submit.
    if (status === 'rejected') return;
    // For approval, call API immediately
    if (status === 'approved') {
      const payload: Record<string, any> = {
        companyName: companyData.companyName,
        platformName: platform,
        status: 'approved',
        approvedHeaders: getDataHeadersForPlatform(platform),
        userId, // Pass userId here
      };
      try {
        await fetch('${BASE_URL}/platform-header-approval/save', {
          method: 'POST',
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        toast({
          title: `Platform approved`,
          description: `Header configuration for ${platform} has been approved.`,
        });
      } catch (err) {
        toast({
          title: "Error",
          description: `Failed to save header approval for ${platform}.`,
        });
      }
    }
  };

  // New: Submit rejection reason and call API
  const handleRejectionReason = (platform: string, reason: string) => {
    setRejectionReasons(prev => ({ ...prev, [platform]: reason }));
  };

  const handleSubmitRejection = async (platform: string) => {
    const reason = rejectionReasons[platform] || '';
    if (!reason.trim()) {
      toast({
        title: "Rejection Reason Required",
        description: "Please provide a rejection reason before submitting.",
        variant: "destructive"
      });
      return;
    }
    const payload: Record<string, any> = {
      companyName: companyData.companyName,
      platformName: platform,
      status: 'rejected',
      rejectionReason: reason,
      userId, // Pass userId here
    };
    try {
      await fetch(BASE_URL+'/platform-header-approval/save', {
        method: 'POST',
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      toast({
        title: `Platform rejected`,
        description: `Header configuration for ${platform} has been rejected.`,
      });
    } catch (err) {
      toast({
        title: "Error",
        description: `Failed to save header rejection for ${platform}.`,
      });
    }
  };

  // Complete
  const completeOnboarding = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      localStorage.setItem("onboardingComplete", "true");
      window.dispatchEvent(new Event("storage"));
      toast({
        title: "Onboarding Complete!",
        description: "Your system is now configured and ready to use.",
      });
      setLocation("/");
    }, 2000);
  };

  // For Download

  const downloadPlatformHeaders = (platform: string): void => {
    const headers: string[] = getDataHeadersForPlatform(platform);
    const csvContent: string = headers.join(',');
    const blob: Blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link: HTMLAnchorElement = document.createElement('a');
    const url: string = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${platform}_data_headers_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast({
      title: "Headers Downloaded",
      description: `${platform} data headers have been downloaded successfully.`,
    });
  };

  // Next/Back Button Logic
  const handleNext = () => {
    if (step === 2) {
      handleAdminSave();
    } else if (step === 3) {
      handleSaveCompanyAndPlatforms();
    } else if (step < totalSteps) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  // For controlled config fields
  const currentConfig = selectedPlatform
    ? // Ensure dataSourceType is always present for the selected platform
      platformConfigs[selectedPlatform] && platformConfigs[selectedPlatform].dataSourceType
        ? platformConfigs[selectedPlatform]
        : { ...platformConfigs[selectedPlatform], dataSourceType: "portal" }
    : {};

  // Render
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col items-center mb-10">
        <div className="h-16 mb-6 flex items-center justify-center px-4 py-2">
          <Logo className="h-12" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900">Welcome to NuvoRetail</h1>
        <p className="mt-2 text-lg text-gray-600">Let's set up your PO Management System</p>
      </div>

      <div className="w-full max-w-4xl">
        <div className="mb-8">
          <Progress value={progress} className="h-2 bg-gray-200" />
          <div className="flex justify-between mt-2 text-sm text-gray-500">
            <span>Step {step} of {totalSteps}</span>
            <span>{Math.round(progress)}% Complete</span>
          </div>
        </div>

        <Card className="w-full">
          {/* STEP 1: COMPANY */}
          {step === 1 && (
            <>
              <CardHeader>
                <CardTitle className="text-2xl">Company Information</CardTitle>
                <CardDescription>
                  Tell us about your company so we can customize your experience
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="company-name">Company Name</Label>
                    <Input 
                      id="company-name" 
                      placeholder="Enter your company name"
                      value={companyData.companyName}
                      onChange={e => setCompanyData(prev => ({ ...prev, companyName: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="industry">Industry</Label>
                    <Select 
                      value={companyData.industry}
                      onValueChange={value => setCompanyData(prev => ({ ...prev, industry: value }))}
                    >
                      <SelectTrigger id="industry">
                        <SelectValue placeholder="Select industry" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="retail">Retail</SelectItem>
                        <SelectItem value="manufacturing">Manufacturing</SelectItem>
                        <SelectItem value="distribution">Distribution</SelectItem>
                        <SelectItem value="fashion">Fashion</SelectItem>
                        <SelectItem value="electronics">Electronics</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="erp-system">Current ERP System</Label>
                  <Select
                    value={companyData.erpSystem}
                    onValueChange={value => setCompanyData(prev => ({ ...prev, erpSystem: value }))}
                  >
                    <SelectTrigger id="erp-system">
                      <SelectValue placeholder="Select ERP system" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sap">SAP</SelectItem>
                      <SelectItem value="oracle">Oracle</SelectItem>
                      <SelectItem value="microsoft">Microsoft Dynamics</SelectItem>
                      <SelectItem value="netsuite">NetSuite</SelectItem>
                      <SelectItem value="epicor">Epicor</SelectItem>
                      <SelectItem value="infor">Infor</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                      <SelectItem value="none">None</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-4">
                  <Label>Select Your Platforms</Label>
                  <p className="text-sm text-gray-600">Choose the e-commerce platforms you work with</p>
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                    {platformsList.map(platform => (
                      <div key={platform} className="flex items-center space-x-2">
                        <Checkbox
                          id={platform.toLowerCase().replace(/\s+/g, '-')}
                          checked={selectedPlatforms.includes(platform)}
                          onCheckedChange={checked => handlePlatformChange(platform, checked as boolean)}
                        />
                        <Label htmlFor={platform.toLowerCase().replace(/\s+/g, '-')}>
                          {platform}
                        </Label>
                      </div>
                    ))}
                  </div>
                  {selectedPlatforms.length > 0 && (
                    <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-700">
                        <strong>Selected:</strong> {selectedPlatforms.join(', ')}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </>
          )}

          {/* STEP 2: ADMIN */}
          {step === 2 && (
            <>
              <CardHeader>
                <CardTitle className="text-2xl">Admin Profile</CardTitle>
                <CardDescription>
                  Set up your profile to personalize your experience
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="first-name">Name</Label>
                    <Input 
                      id="first-name" 
                      placeholder="Enter your name"
                      value={adminData.name}
                      onChange={e => setAdminData(prev => ({ ...prev, name: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input 
                      id="password" 
                      placeholder="Enter your password"
                      value={adminData.password}
                      onChange={e => setAdminData(prev => ({ ...prev, password: e.target.value }))}
                      type="password"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input 
                    id="email"
                    type="email"
                    placeholder="your.email@example.com"
                    value={adminData.email}
                    onChange={e => setAdminData(prev => ({ ...prev, email: e.target.value }))}
                  />
                </div>
              </CardContent>
            </>
          )}

          {/* STEP 3: PLATFORM CONFIG */}
          {step === 3 && (
            <>
              <CardHeader>
                <CardTitle className="text-2xl">Platform Data Configuration</CardTitle>
                <CardDescription>
                  Configure data sources for your selected platforms
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {selectedPlatforms.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Database className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Platforms Selected</h3>
                    <p>Please go back to step 1 and select at least one platform to configure.</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Platform Selector */}
                    <div className="space-y-2">
                      <Label htmlFor="platform-select">Select Platform to Configure</Label>
                      <Select value={selectedPlatform} onValueChange={setSelectedPlatform}>
                        <SelectTrigger id="platform-select">
                          <SelectValue placeholder="Choose a platform to configure" />
                        </SelectTrigger>
                        <SelectContent>
                          {selectedPlatforms.map(platform => (
                            <SelectItem key={platform} value={platform}>{platform}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-sm text-gray-600">
                        Configure each platform individually. Selected platforms: {selectedPlatforms.join(', ')}
                      </p>
                    </div>
                    {/* Config fields for the selected platform */}
                    {selectedPlatform && (
                      <Card className="border-2 border-primary/20 bg-primary/5">
                        <CardHeader className="pb-4">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <Building2 className="h-5 w-5 text-primary" />
                            {selectedPlatform} Configuration
                          </CardTitle>
                          <CardDescription>
                            Configure how'd like to receive PO data from {selectedPlatform}
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          {/* Data Source Method */}
                          <div className="space-y-3">
                            <Label>Data Source Method</Label>
                            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                              {["portal", "api", "email"].map(type => (
                                <Card
                                  key={type}
                                  className={`cursor-pointer border relative ${currentConfig.dataSourceType === type ? "border-primary bg-primary/10" : "border-gray-200"}`}
                                  onClick={() => {
                                    // Only update if changing type, to avoid resetting on every click
                                    if (currentConfig.dataSourceType !== type) handleDataSourceTypeChange(type);
                                  }}
                                >
                                  {type === "portal" && currentConfig.dataSourceType === "portal" && (
                                    <div className="absolute -top-2 -right-2 bg-primary text-white text-xs px-2 py-1 rounded-full font-medium z-10">
                                      Recommended
                                    </div>
                                  )}
                                  <CardContent className="p-4 flex flex-col items-center text-center">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-2 ${currentConfig.dataSourceType === type ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600'}`}>
                                      {type === "portal" && <LinkIcon className="h-4 w-4" />}
                                      {type === "api" && <Database className="h-4 w-4" />}
                                      {type === "email" && <Mail className="h-4 w-4" />}
                                    </div>
                                    <h4 className="text-sm font-medium">
                                      {type === "portal" ? "Portal Access" : type === "api" ? "API Integration" : "Email Reports"}
                                    </h4>
                                    <p className="text-xs text-gray-500 mt-1">
                                      {type === "portal" ? "Direct portal connection" : type === "api" ? "Direct API access" : "Automated emails"}
                                    </p>
                                  </CardContent>
                                </Card>
                              ))}
                            </div>
                          </div>
                          <Separator />
                          {/* Config fields per type */}
                          {currentConfig.dataSourceType === "portal" && (
                            <div className="space-y-3">
                              <div className="space-y-2">
                                <Label htmlFor="portal-url">{selectedPlatform} Portal URL</Label>
                                <Input
                                  id="portal-url"
                                  placeholder={`https://${selectedPlatform.toLowerCase()}-seller.com`}
                                  value={currentConfig.portalUrl || ""}
                                  onChange={e => handlePlatformConfigChange("portalUrl", e.target.value)}
                                />
                              </div>
                              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                <div className="space-y-2">
                                  <Label htmlFor="username">Username/Email</Label>
                                  <Input
                                    id="username"
                                    placeholder="Enter your username"
                                    value={currentConfig.username || ""}
                                    onChange={e => handlePlatformConfigChange("username", e.target.value)}
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label htmlFor="password">Password</Label>
                                  <Input
                                    id="password"
                                    type="password"
                                    placeholder="Enter your password"
                                    value={currentConfig.password || ""}
                                    onChange={e => handlePlatformConfigChange("password", e.target.value)}
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                          {currentConfig.dataSourceType === "api" && (
                            <div className="space-y-3">
                              <div className="space-y-2">
                                <Label htmlFor="api-endpoint">{selectedPlatform} API Endpoint</Label>
                                <Input
                                  id="api-endpoint"
                                  placeholder={`https://api.${selectedPlatform.toLowerCase()}.com/v1/orders`}
                                  value={currentConfig.apiEndpoint || ""}
                                  onChange={e => handlePlatformConfigChange("apiEndpoint", e.target.value)}
                                />
                              </div>
                              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                <div className="space-y-2">
                                  <Label htmlFor="api-key">API Key/Token</Label>
                                  <Input
                                    id="api-key"
                                    type="password"
                                    placeholder="Enter your API key"
                                    value={currentConfig.apiKey || ""}
                                    onChange={e => handlePlatformConfigChange("apiKey", e.target.value)}
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label htmlFor="refresh-interval">Sync Interval</Label>
                                  <Select
                                    value={currentConfig.refreshInterval || ""}
                                    onValueChange={value => handlePlatformConfigChange("refreshInterval", value)}
                                  >
                                    <SelectTrigger id="refresh-interval">
                                      <SelectValue placeholder="Select interval" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="5">5 minutes</SelectItem>
                                      <SelectItem value="15">15 minutes</SelectItem>
                                      <SelectItem value="30">30 minutes</SelectItem>
                                      <SelectItem value="60">1 hour</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                            </div>
                          )}
                          {currentConfig.dataSourceType === "email" && (
                            <div className="space-y-3">
                              <div className="space-y-2">
                                <Label htmlFor="email-address">{selectedPlatform} Report Email</Label>
                                <Input
                                  id="email-address"
                                  type="email"
                                  placeholder={`reports@${selectedPlatform.toLowerCase()}.com`}
                                  value={currentConfig.emailAddress || ""}
                                  onChange={e => handlePlatformConfigChange("emailAddress", e.target.value)}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="email-format">Expected Format</Label>
                                <Select
                                  value={currentConfig.emailFormat || ""}
                                  onValueChange={value => handlePlatformConfigChange("emailFormat", value)}
                                >
                                  <SelectTrigger id="email-format">
                                    <SelectValue placeholder="Select format" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="csv">CSV Attachment</SelectItem>
                                    <SelectItem value="excel">Excel Attachment</SelectItem>
                                    <SelectItem value="json">JSON Data</SelectItem>
                                    <SelectItem value="xml">XML Format</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    )}
                  </div>
                )}
              </CardContent>
            </>
          )}

          {/* STEP 4: APPROVAL */}
          {step === 4 && (
            <>
              <CardHeader>
                <CardTitle className="text-2xl">Platform Data Approval</CardTitle>
                <CardDescription>
                  Review and approve the data headers fetched from your selected platforms
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {selectedPlatforms.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Database className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Platforms Selected</h3>
                    <p>Please go back to step 1 and select at least one platform to configure.</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {selectedPlatforms.map(platform => {
                      const headers = getDataHeadersForPlatform(platform);
                      const approvalStatus = platformApprovalStatus[platform] || 'pending';
                      return (
                        <Card key={platform} className={`border-2 ${
                          approvalStatus === 'approved' ? 'border-green-200 bg-green-50' :
                          approvalStatus === 'rejected' ? 'border-red-200 bg-red-50' : 'border-gray-200'
                        }`}>
                          <CardHeader className="pb-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Building2 className="h-5 w-5 text-primary" />
                                <CardTitle className="text-lg">{platform} Data Headers</CardTitle>
                                {approvalStatus === 'approved' && (
                                  <Badge className="bg-green-100 text-green-800 border-green-200">
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    Approved
                                  </Badge>
                                )}
                                {approvalStatus === 'rejected' && (
                                  <Badge className="bg-red-100 text-red-800 border-red-200">
                                    <XCircle className="h-3 w-3 mr-1" />
                                    Rejected
                                  </Badge>
                                )}
                                {approvalStatus === 'pending' && (
                                  <Badge variant="outline" className="bg-yellow-50 text-yellow-800 border-yellow-200">
                                    <Clock className="h-3 w-3 mr-1" />
                                    Pending Review
                                  </Badge>
                                )}
                              </div>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => downloadPlatformHeaders(platform)}
                              >
                                <Download className="h-4 w-4 mr-1" />
                                Download Headers
                              </Button>
                            </div>
                            <CardDescription>
                              Data structure fetched from {platform}. Review and approve these headers.
                            </CardDescription>
                          </CardHeader>
                          <CardContent className="space-y-4">
                            <div className="space-y-3">
                              <Label className="text-sm font-medium">Data Headers ({headers.length} fields)</Label>
                              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 p-4 bg-gray-50 rounded-lg max-h-40 overflow-y-auto">
                                {headers.map((header, index) => (
                                  <div key={index} className="text-xs bg-white px-2 py-1 rounded border text-gray-700 font-mono">
                                    {header}
                                  </div>
                                ))}
                              </div>
                            </div>
                            <div className="flex items-center gap-3 pt-4 border-t">
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-green-600 border-green-200 hover:bg-green-50"
                                onClick={() => handlePlatformApproval(platform, 'approved')}
                                disabled={approvalStatus === 'approved'}
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Approve Headers
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-600 border-red-200 hover:bg-red-50"
                                onClick={() => handlePlatformApproval(platform, 'rejected')}
                                disabled={approvalStatus === 'rejected'}
                              >
                                <XCircle className="h-4 w-4 mr-1" />
                                Reject Headers
                              </Button>
                            </div>
                            {approvalStatus === 'rejected' && (
                              <div className="space-y-2 pt-2">
                                <Label htmlFor={`rejection-reason-${platform}`} className="text-sm">
                                  Rejection Reason
                                </Label>
                                <Input
                                  id={`rejection-reason-${platform}`}
                                  placeholder="Please specify the reason for rejection..."
                                  value={rejectionReasons[platform] || ''}
                                  onChange={e => handleRejectionReason(platform, e.target.value)}
                                  className="text-sm"
                                />
                                <Button
                                  size="sm"
                                  className="mt-2"
                                  onClick={() => handleSubmitRejection(platform)}
                                  disabled={!rejectionReasons[platform] || !rejectionReasons[platform].trim()}
                                >
                                  Submit Rejection
                                </Button>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </>
          )}

          {/* STEP 5: COMPLETE */}
          {step === 5 && (
            <>
              <CardHeader>
                <CardTitle className="text-2xl">Complete Setup</CardTitle>
                <CardDescription>
                  Your NuvoRetail system is ready to go!
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="text-center py-8">
                  <CheckCircle className="h-16 w-16 mx-auto mb-4 text-green-600" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Setup Complete!</h3>
                  <p className="text-gray-600 mb-6">
                    Your NuvoRetail Purchase Order management system is ready to use.
                  </p>
                  <div className="bg-gray-50 rounded-lg p-6 mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Configuration Summary</h4>
                    <div className="space-y-2 text-sm text-gray-600">
                      <div className="flex justify-between">
                        <span>Admin:</span>
                        <span className="font-medium">{adminData.name} ({adminData.email})</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Company:</span>
                        <span className="font-medium">{companyData.companyName}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Industry:</span>
                        <span className="font-medium capitalize">{companyData.industry}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>ERP System:</span>
                        <span className="font-medium capitalize">{companyData.erpSystem}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Platforms:</span>
                        <span className="font-medium">{selectedPlatforms.join(", ")}</span>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <h4 className="font-semibold text-gray-900">What's Next?</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div className="p-4 border rounded-lg">
                        <BarChart4 className="h-8 w-8 text-purple-600 mb-2 mx-auto" />
                        <p className="font-medium">Start Managing POs</p>
                        <p className="text-gray-600">Track and validate purchase orders</p>
                      </div>
                      <div className="p-4 border rounded-lg">
                        <Database className="h-8 w-8 text-purple-600 mb-2 mx-auto" />
                        <p className="font-medium">Monitor Inventory</p>
                        <p className="text-gray-600">Keep track of stock levels</p>
                      </div>
                      <div className="p-4 border rounded-lg">
                        <LucideFileCheck className="h-8 w-8 text-purple-600 mb-2 mx-auto" />
                        <p className="font-medium">Generate Reports</p>
                        <p className="text-gray-600">Analyze performance metrics</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </>
          )}

          {/* FOOTER NAVIGATION */}
          <CardFooter className="flex justify-between">
            <Button 
              variant="outline" 
              onClick={handleBack}
              disabled={step === 1 || loading}
            >
              <ChevronLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            {step < totalSteps ? (
              <Button onClick={handleNext} disabled={loading}>
                {loading ? "Saving..." : "Next"}
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            ) : (
              <Button onClick={completeOnboarding} disabled={loading}>
                {loading ? "Setting up..." : "Complete Setup"}
                {loading ? (
                  <Upload className="ml-2 h-4 w-4 animate-spin" />
                ) : (
                  <LucideFileCheck className="ml-2 h-4 w-4" />
                )}
              </Button>
            )}
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default OnboardingPage;
